/**
 * @module utils/date-helper
 */

const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
export const listOfMonths = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];
const monthShorts = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'];

/**
 * Return human readable timestamp format
 *
 * @example
 * humanizeTimestamp('2017-06-23T14:15:53.886+07:00', '%day% %dd% %mmm% %yyyy% - %hh%:%mi% WIB')
 * // => Jumat 23 Jun 2017 - 14:15 WIB
 *
 * @param  {Date | String} timestamp timestamp format
 * @param  {String} format specific wanted format to humanize the timestamp (in-casesensitive)
 *
 * @return {String}
 */
export function humanizeTimestamp(timestamp: Date | string, format = '%day%, %dd% %month% %yyyy%') {
  const dateTime = new Date(timestamp);
  const timeMapping = {
    '%hh%': `0${dateTime.getHours()}`.slice(-2),
    '%mi%': `0${dateTime.getMinutes()}`.slice(-2),
    '%dd%': `0${dateTime.getDate()}`.slice(-2),
    '%day%': days[dateTime.getDay()],
    '%mm%': `0${dateTime.getMonth() + 1}`.slice(-2),
    '%mmm%': monthShorts[dateTime.getMonth()],
    '%month%': listOfMonths[dateTime.getMonth()],
    '%yyyy%': dateTime.getFullYear().toString(),
    '%Ddd%': `${days[dateTime.getDay()]}`.slice(0, 3),
  };
  return format.replace(/%(.*?)%/gi, n => timeMapping[n as keyof typeof timeMapping]);
}

const prependZero = (number: string | number) =>
  Number(number) < 10 ? `0${Number(number).toString()}` : Number(number).toString();

/**
 * Time Filter
 * @param value {string} ISO 8601 format, e.g '2017-01-31T02:30:00+07:00'
 * @return hour:minute e.g '02:30'
 */
export const customTime = (value: Date | string) => {
  if (value === undefined) {
    throw new Error('value cant be null');
  }

  if (typeof value === 'number') {
    throw new TypeError('type must be string or date');
  }

  if (typeof value === 'string') {
    return value.substring(11, 16);
  } else if (value instanceof Date) {
    return `${prependZero(value.getHours())}:${prependZero(value.getMinutes())}`;
  }
  const date = new Date(value);
  return `${prependZero(date.getHours())}:${prependZero(date.getMinutes())}`;
};

export function timeDiff(start: Date | number, end: Date | number, unit = 'ms') {
  const diff = new Date(end).valueOf() - new Date(start).valueOf();

  switch (unit) {
    case 's':
      return diff / 1000;
    default:
      return diff;
  }
}

export function timeDiffSeconds(start: Date | number, end: Date | number) {
  return timeDiff(start, end, 's');
}

export function listOfLast100Years() {
  const years: number[] = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 100; i -= 1) {
    years.push(i);
  }
  return years;
}

/**
 * Parse UTC Time into JS Date
 *
 * @example
 * parseTime('03:25:10 PM', new Date('2020'))
 * // => Date with value 2020-01-01T15:25:10.000Z (toISOString)
 *
 * @param  {String} time in AM/PM mode OR 24 hour mode if it is from Zendesk Agent Workspace Tickets
 * @param  {Date} date to combine (optional)
 * @param  {Boolean} is24HourFormat
 * @param  {Boolean} isUtc
 *
 * @return {Date} Current/specified date and specified time in JS Date
 */
export function parseTime(time: string, date: Date, is24HourFormat: boolean = false, isUtc: boolean = true) {
  if (is24HourFormat) {
    const match = time.match(/(\d{2}):(\d{2}):(\d{2})/);
    const [, hour, minute, second]: string[] = match || [];
    const datetime = new Date(date) || new Date();

    if (isUtc) {
      datetime.setUTCHours(Number(hour));
      datetime.setUTCMinutes(Number(minute));
      datetime.setUTCSeconds(Number(second));
      return datetime;
    }

    // isUtc === false
    datetime.setHours(Number(hour));
    datetime.setMinutes(Number(minute));
    datetime.setSeconds(Number(second));
    return datetime;
  }

  const match = time.match(/(\d{2}):(\d{2}):(\d{2}) ([AP]M)/);
  const [, hour, minute, second, period]: string[] = match || [];
  const hour24 = period === 'AM' ? hour : Number(hour) + 12;
  const datetime = new Date(date) || new Date();
  datetime.setUTCHours(Number(hour24));
  datetime.setUTCMinutes(Number(minute));
  datetime.setUTCSeconds(Number(second));
  return datetime;
}

export function dayToTime(day: number) {
  return day * 1000 * 60 * 60 * 24;
}
export function hourToTime(hour: number) {
  return hour * 1000 * 60 * 60;
}
export function minuteToTime(minute: number) {
  return minute * 1000 * 60;
}

export function timeToDay(time: number) {
  return Math.floor(time / (1000 * 60 * 60 * 24));
}
export function timeToHour(time: number) {
  return Math.floor(time / (1000 * 60 * 60));
}
export function timeToMinute(time: number) {
  return Math.floor(time / (1000 * 60));
}

export default {
  listOfMonths,
  listOfLast100Years,
  humanizeTimestamp,
  customTime,
  timeDiff,
  timeDiffSeconds,
  parseTime,
  dayToTime,
  hourToTime,
  minuteToTime,
  timeToDay,
  timeToHour,
  timeToMinute,
};
